import React, { useMemo } from 'react';

import {
  useCompanies,
  useFormattedUser,
  useUser,
  useUserByAutodeskId,
} from '@adsk/alloy-provider';
import Avatar from '@adsk/alloy-react-avatar';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { MentionData, MENTION_TYPES } from '../../consts';

export const MentionUserTooltip = ({
  mention,
  children,
}: {
  mention?: MentionData;
  children?: React.ReactNode;
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const companies = useCompanies();
  const { user: currentUser } = useUser();
  const { user } = useUserByAutodeskId(mention?.id?.toString() || '', {
    includeDeleted: true,
  });

  const { formatDisplayUser } = useFormattedUser();

  const formattedUserName = useMemo(
    () =>
      (mention && formatDisplayUser(mention.id).displayName) ||
      mention?.name ||
      T.translate('NOT_AVAILABLE')?.toString(),
    [formatDisplayUser, mention, T],
  );
  return (
    <Tooltip
      content={
        <div
          data-testid={`MentionUserTooltip_Content_${mention?.id}`}
          css={{
            display: 'flex',
            alignItems: 'center',
            wordBreak: 'break-all',
          }}
        >
          <Avatar
            name={formattedUserName}
            imageUrl={user?.imageUrl}
            style={{
              marginRight: 10,
            }}
          />
          <span>
            <div
              css={[
                theme.typography.labelMedium,
                {
                  color: theme.colors.charcoal900,
                },
              ]}
            >
              {formattedUserName}
            </div>
            <div
              css={[
                theme.typography.labelMedium,
                {
                  color: theme.colors.charcoal700,
                },
              ]}
            >
              {
                companies.data.find((company) => company.id === user?.companyId)
                  ?.name
              }
            </div>
          </span>
        </div>
      }
      triggerTarget={Tooltip.TRIGGER_TARGETS.TOOLTIP}
      variant={Tooltip.VARIANTS.LIGHT}
    >
      <span
        data-testid={`MentionUserTooltip_Trigger_${mention?.id}`}
        css={{
          ...(mention?.type === MENTION_TYPES.USER &&
            currentUser?.id === mention?.id && {
              background: theme.colors.yellowOrange100,
              '&:hover': {
                background: theme.colors.yellowOrange300,
              },
            }),
        }}
      >
        @{children || formattedUserName}
      </span>
    </Tooltip>
  );
};
